import { Box, Button, Flex, Link, Textarea } from "@chakra-ui/react";
import { useState } from "react";
import { MdStar } from "react-icons/md";
import { MdsStarRound } from "react-icons-with-materialsymbols/mds";

import { useSubmitFeedbackMutation } from "@/features/user-manager/api/users-api";
import { useAppDispatch, useAppSelector } from "@/reduxHooks.ts";
import { currentUserMetadata } from "@/slices/auth-slice";
import { ModalTypes, openModal } from "@/slices/modal-slice";
import { AnalyticsEvents } from "@/utils/analytics-events";
import { analyticsService } from "@/utils/analytics-service";
import { FEEDBACK_TYPE } from "@/utils/enums";

import { useShowToast } from "../../toast";

interface FeedbackModalBodyProps {
  onClose: () => void;
}

export const FeedbackModalBody = ({ onClose }: FeedbackModalBodyProps) => {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();
  const toast = useShowToast();
  const dispatch = useAppDispatch();
  const userMetadata = useAppSelector(currentUserMetadata);
  const { modalProps } = useAppSelector((state) => state.rootReducer.modals);

  const { feedbackType } = modalProps as { feedbackType: FEEDBACK_TYPE };
  const currentUser = useAppSelector(currentUserMetadata);

  const validateFeedback = () => {
    if (rating === 0) {
      toast({
        title: "Please add a rating",
        status: "error",
      });
      return false;
    }
    if (feedback.length > 2000) {
      toast({
        title: "Please limit your feedback to 2000 characters",
        status: "warning",
      });
      return false;
    }
    return true;
  };

  const onRatingChange = (value: number) => {
    if (isLoading) return;
    setRating(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (!validateFeedback()) {
        return;
      }

      await submitFeedback({
        starrating: rating,
        ...(feedback && { feedback }),
        ...(feedbackType !== FEEDBACK_TYPE.SUBMIT_FEEDBACK && {
          trial_feedback: true,
        }),
      }).unwrap();
      toast({
        title: "Thank you for your feedback!",
        status: "success",
      });
      setRating(0);
      setFeedback("");
      onClose();

      if (rating >= 4 && userMetadata?.isTrialUser) {
        dispatch(
          openModal({
            modalType: ModalTypes.UNLOCK_APP,
            modalProps: {},
          })
        );
      }
    } catch (error) {
      console.error("Failed to submit feedback:", error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Flex className="flex-col gap-4 px-5">
        {feedbackType === FEEDBACK_TYPE.TRAIL_ENDING && (
          <Box
            className="font-normal"
            onClick={() => {
              analyticsService.trackEvent(AnalyticsEvents.CONTACT_US.FEEDBACK);
            }}
          >
            Your trial is almost over! We'd love to hear your feedback. If you'd
            like to continue,{" "}
            <Link
              className="!underline font-medium"
              href="https://clootrack.com/request-demo"
              target="_blank"
            >
              contact our sales team
            </Link>{" "}
            for upgrade options.
          </Box>
        )}
        <Box className="">How would you rate your experience?</Box>
        <Flex className="gap-2 justify-center">
          {[1, 2, 3, 4, 5].map((star) => (
            <Box
              className="cursor-pointer"
              key={star}
              onClick={() => onRatingChange(star)}
            >
              {star <= rating ? (
                <MdStar className="text-gray-800" size={32} />
              ) : (
                <MdsStarRound className="text-gray-800" size={32} />
              )}
            </Box>
          ))}
        </Flex>
        <Box className="">Any additional comments or suggestions?</Box>
        <Textarea
          className="font-normal"
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Share your thoughts..,"
          value={feedback}
        />
        <Button
          className="my-4 self-end"
          colorScheme="dark"
          isDisabled={isLoading}
          isLoading={isLoading}
          type="submit"
        >
          Submit
        </Button>
      </Flex>
    </form>
  );
};
